import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminLayout from '../components/AdminLayout'
import {
  ClipboardList, X, CheckCircle, AlertCircle, Loader,
  Search, Filter, ChevronLeft, ChevronRight, Calendar,
  MapPin, Truck, User, Weight, Clock, Eye, Check,
  XCircle, RefreshCw, Grid, Table, CalendarDays, Zap,
  AlertTriangle, TrendingUp, Users, Car, DollarSign, CalendarCheck, Bell
} from 'lucide-react'
import {
  FetchBookingRequests,
  AdminFindNearestDriver,
  AdminAcceptBooking,
  AdminCancelBooking,
  ResetAdminBookingRequest,
} from '../actions/AdminBookingRequestAction'
import { withAssignmentLock } from '../utils/assignmentLock'

// ─── Stat Card Component ─────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue:   { bg: 'bg-blue-50',    iconBg: 'bg-blue-100',    text: 'text-blue-600',    border: 'border-blue-200'   },
    purple: { bg: 'bg-purple-50',  iconBg: 'bg-purple-100',  text: 'text-purple-600',  border: 'border-purple-200' },
    indigo: { bg: 'bg-indigo-50',  iconBg: 'bg-indigo-100',  text: 'text-indigo-600',  border: 'border-indigo-200' },
    teal:   { bg: 'bg-teal-50',    iconBg: 'bg-teal-100',    text: 'text-teal-600',    border: 'border-teal-200'   },
    yellow: { bg: 'bg-yellow-50',  iconBg: 'bg-yellow-100',  text: 'text-yellow-600',  border: 'border-yellow-200' },
    green:  { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200'},
    red:    { bg: 'bg-red-50',     iconBg: 'bg-red-100',     text: 'text-red-600',     border: 'border-red-200'    },
  }
  const colors = colorClasses[color] || colorClasses.blue
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${colors.iconBg} p-3 rounded-xl`}>
          <Icon size={20} className={colors.text} />
        </div>
      </div>
    </div>
  )
}

// ─── Tab Button Component ────────────────────────────────────────────────────
function TabButton({ active, onClick, children, icon: Icon, count, activeColor }) {
  const activeStyle = activeColor === 'teal'
    ? 'bg-white text-teal-700 shadow-sm border border-teal-200'
    : 'bg-white text-gray-900 shadow-sm border border-gray-200'
  const countStyle = activeColor === 'teal'
    ? 'bg-teal-100 text-teal-700'
    : active ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all rounded-lg ${
        active ? activeStyle : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
      }`}
    >
      {Icon && <Icon size={16} />}
      {children}
      {count !== undefined && (
        <span className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${countStyle}`}>
          {count}
        </span>
      )}
    </button>
  )
}

// ─── Booking Card Component (Mobile View) ───────────────────────────────────
function BookingCard({ booking, onView, onAccept, onCancel, statusColor, normalizeDate, hasConflict, isBlocked, showActions, showTonnage, willAutoAccept, autoAcceptTime, acceptedReqIds }) {
  const id = String(booking.reqId || booking.bId)
  const isAutoAccepted = acceptedReqIds?.has(id)
  const statusClass = statusColor(booking.status)
  return (
    <div className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
      hasConflict
        ? isBlocked ? 'border-red-200 bg-red-50/30' : 'border-yellow-200 bg-yellow-50/30'
        : willAutoAccept
        ? 'border-purple-200 bg-purple-50/30'
        : 'border-gray-100'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
              {booking.status === 'Pending'   && <Clock size={10} />}
              {booking.status === 'Active'    && <CheckCircle size={10} />}
              {booking.status === 'Completed' && <Check size={10} />}
              {booking.status === 'Cancel'    && <XCircle size={10} />}
              {booking.status}
            </span>
            {hasConflict && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                <AlertTriangle size={10} />
                {isBlocked ? 'Blocked' : 'Conflict'}
              </span>
            )}
            {willAutoAccept && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                <Bell size={10} />
                Auto in {autoAcceptTime}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <User size={12} />
            <span>{booking.bookingSno}</span>
            <span>•</span>
            <Calendar size={12} />
            <span>{normalizeDate(booking.bookingDT)}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        {showTonnage && (
          <div>
            <p className="text-gray-500 mb-0.5">Tonnage</p>
            <p className="font-semibold text-gray-900">{booking.tonnage || '—'} tons</p>
          </div>
        )}
        <div>
          <p className="text-gray-500 mb-0.5">Booking Type</p>
          <p className="font-semibold text-gray-900">{booking.BookingStatus || '-'}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Route</p>
          <p className="font-semibold text-gray-900 flex items-center gap-1">
            <MapPin size={12} className="text-gray-400" />
            <span className="truncate">{booking.From || '—'} → {booking.TO || '—'}</span>
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Time Slot</p>
          <p className="font-semibold text-gray-900 text-xs">
            {booking.bsTime ? new Date(booking.bsTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            {' → '}
            {booking.beTime ? new Date(booking.beTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onView(booking)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          <Eye size={14} />
          View Details
        </button>
        {showActions && booking.status === 'Pending' && !acceptedReqIds.has(String(booking.reqId || booking.bId)) && (
          <>
            <button
              onClick={() => !isBlocked && onAccept(booking)}
              disabled={isBlocked}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition ${
                isBlocked
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'text-white bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <CheckCircle size={14} />
              Accept
            </button>
            <button
              onClick={() => onCancel(booking.reqId || booking.bId)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
            >
              <XCircle size={14} />
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Helper Component ────────────────────────────────────────────────────────
function InfoField({ label, value, badge }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {badge ? (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
          {value}
        </span>
      ) : (
        <p className="text-sm font-medium text-gray-900 break-words">{value || '-'}</p>
      )}
    </div>
  )
}

// ─── Helper Functions ────────────────────────────────────────────────────────
const parseBookingTime = (val) => {
  if (!val) return null
  const d = new Date(val)
  return isNaN(d.getTime()) ? null : d
}

const getRequestPlacedTime = (booking) => {
  if (!booking) return null
  // Auto-accept window must start from request placement time.
  // bookingDT is the canonical created/requested timestamp.
  return parseBookingTime(booking.bookingDT)
}

const doTimesOverlap = (a, b) => {
  const aStart = parseBookingTime(a.bsTime)
  const aEnd   = parseBookingTime(a.beTime)
  const bStart = parseBookingTime(b.bsTime)
  const bEnd   = parseBookingTime(b.beTime)
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart < bEnd && bStart < aEnd
}

const normalizeTonnage = (t) =>
  String(t || '').toLowerCase().replace(/[^0-9.]/g, '')

const AUTO_ACCEPT_MINUTES = 3

const getConflictingReqIds = (bookings) => {
  const conflicts = new Set()
  for (let i = 0; i < bookings.length; i++) {
    for (let j = i + 1; j < bookings.length; j++) {
      const a = bookings[i]
      const b = bookings[j]
      if (normalizeTonnage(a.tonnage) === normalizeTonnage(b.tonnage) && doTimesOverlap(a, b)) {
        conflicts.add(String(a.reqId || a.bId))
        conflicts.add(String(b.reqId || b.bId))
      }
    }
  }
  return conflicts
}

// ─── Filter Components ───────────────────────────────────────────────────────
function FilterInput({ placeholder, value, onChange, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white`}
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
    >
      <option value="">All Status</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  )
}

function FilterDate({ value, onChange }) {
  return (
    <div className="relative">
      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

// ─── Table Row Component ─────────────────────────────────────────────────────
const TableRow = ({
  booking, onView, onAccept, onCancel,
  statusColor, normalizeDate,
  conflictIds, blockedIds, acceptedReqIds,
  showActions, showTonnage, readOnly,
  willAutoAccept, autoAcceptTime,
}) => {
  const id          = String(booking.reqId || booking.bId)
  const hasConflict = conflictIds.has(id)
  // const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
  const isBlocked = blockedIds.has(id)
  const isAutoAccepted = acceptedReqIds.has(id)

  return (
    <tr className={`hover:bg-gray-50 transition ${
      hasConflict
        ? (isBlocked ? 'bg-red-50/30' : 'bg-yellow-50/30')
        : willAutoAccept ? 'bg-purple-50/30' : ''
    }`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
          {hasConflict && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
              <AlertTriangle size={10} />
              {isBlocked ? 'Blocked' : 'Conflict'}
            </span>
          )}
          {willAutoAccept && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              <Bell size={10} />
              Auto in {autoAcceptTime}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 font-medium">{booking.bookingSno}</td>
      {showTonnage && (
        <td className="py-3 px-4">
          <span className="inline-flex px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-lg">
            {booking.tonnage || '—'} tons
          </span>
        </td>
      )}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin size={14} className="text-gray-400 shrink-0" />
          <span className="truncate max-w-[180px]">{booking.From || '—'} → {booking.TO || '—'}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <Clock size={12} className="text-gray-400" />
          <span>
            {booking.bsTime ? new Date(booking.bsTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            {' → '}
            {booking.beTime ? new Date(booking.beTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${statusColor(booking.status)}`}>
          {booking.status === 'Pending'   && <Clock size={10} />}
          {booking.status === 'Active'    && <CheckCircle size={10} />}
          {booking.status === 'Completed' && <Check size={10} />}
          {booking.status === 'Cancel'    && <XCircle size={10} />}
          {booking.status}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          {booking.BookingStatus === 'Real-time Booking' ? <Zap size={12} /> : <CalendarDays size={12} />}
          {booking.BookingStatus || '-'}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">{normalizeDate(booking.bookingDT)}</td>
      <td className="py-3 px-4">
        <div className="flex gap-2">
          <button
            onClick={() => onView(booking)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <Eye size={12} /> View
          </button>

          {/* Check for !isAutoAccepted to hide the accept button when the booking has been manually accepted */}
          {/* CONSOLE LOG */}
          { console.log("ID 1033 should show accept", {
            id,
            status: booking.status,
            accepted_by: booking.accepted_by,
            isAutoAccepted,
            isBlocked,
            showActions
          }) }

         {showActions && !readOnly && booking.status === 'Pending' && !acceptedReqIds.has(String(booking.reqId || booking.bId)) && (
            <>
              <button
                onClick={() => !isBlocked && onAccept(booking)}
                disabled={isBlocked}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                  isBlocked
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-white bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <CheckCircle size={12} /> Accept
              </button>
              <button
                onClick={() => onCancel(booking.reqId || booking.bId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
              >
                <XCircle size={12} /> Cancel
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Reusable Booking Section ─────────────────────────────────────────────────
function BookingSection({
  isLoading, error, filteredData, viewMode, setViewMode,
  currentPage, setCurrentPage, rowsPerPage,
  conflictIds, blockedIds, acceptedReqIds,
  onView, onAccept, onCancel,
  statusColor, normalizeDate,
  showActions, showTonnage, readOnly,
  reqIdLabel,
  filterBar,
  getWillAutoAccept,
  getAutoAcceptTimeRemaining,
}) {
  const totalPagesCount = Math.max(1, Math.ceil(filteredData.length / rowsPerPage))
  const pageData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)

  return (
    <>
      {filterBar}

      {/* View Toggle */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {filteredData.length} booking{filteredData.length !== 1 ? 's' : ''} found
        </p>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={14} className="inline mr-1.5" />Table View
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={14} className="inline mr-1.5" />Card View
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="text-gray-400 animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Loading bookings...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
              <AlertCircle size={16} />{error}
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-full">
                <ClipboardList size={32} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-500">No booking requests found</p>
            </div>
          </div>
        ) : (
          <>
            {/* Table View */}
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{reqIdLabel || 'Req ID'}</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booked By</th>
                    {showTonnage && <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>}
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Slot</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageData.map((b) => {
                    const id = String(b.reqId || b.bId)
                    const hasConflict = conflictIds.has(id)
                    const willAutoAccept = getWillAutoAccept ? getWillAutoAccept(b) : false
                    const autoAcceptTime = willAutoAccept && getAutoAcceptTimeRemaining ? getAutoAcceptTimeRemaining(b) : null
                    return (
                      <TableRow
                        key={b.reqId || b.bId}
                        booking={b}
                        onView={onView}
                        onAccept={onAccept}
                        onCancel={onCancel}
                        statusColor={statusColor}
                        normalizeDate={normalizeDate}
                        conflictIds={conflictIds}
                        blockedIds={blockedIds}
                        acceptedReqIds={acceptedReqIds}
                        showActions={showActions}
                        showTonnage={showTonnage}
                        readOnly={readOnly}
                        willAutoAccept={willAutoAccept}
                        autoAcceptTime={autoAcceptTime}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Card View */}
            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-4 space-y-3`}>
              {pageData.map((b) => {
                const id          = String(b.reqId || b.bId)
                const hasConflict = conflictIds.has(id)
                // const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
                const isBlocked = blockedIds.has(id)
                const willAutoAccept = getWillAutoAccept ? getWillAutoAccept(b) : false
                const autoAcceptTime = willAutoAccept && getAutoAcceptTimeRemaining ? getAutoAcceptTimeRemaining(b) : null
                return (
                  <BookingCard
                    key={id}
                    booking={b}
                    onView={onView}
                    onAccept={onAccept}
                    onCancel={onCancel}
                    statusColor={statusColor}
                    normalizeDate={normalizeDate}
                    hasConflict={hasConflict}
                    isBlocked={isBlocked}
                    showActions={showActions}
                    showTonnage={showTonnage}
                    willAutoAccept={willAutoAccept}
                    autoAcceptTime={autoAcceptTime}
                    acceptedReqIds={acceptedReqIds}
                  />
                )
              })}
            </div>

            {/* Pagination */}
            {filteredData.length > rowsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-700">{(currentPage - 1) * rowsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium text-gray-700">{Math.min(currentPage * rowsPerPage, filteredData.length)}</span>
                  {' '}of{' '}
                  <span className="font-medium text-gray-700">{filteredData.length}</span> results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={14} />Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm text-gray-600">
                    Page {currentPage} of {totalPagesCount}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPagesCount, p + 1))}
                    disabled={currentPage === totalPagesCount}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next<ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminBooking() {
  const dispatch    = useDispatch()
  const { authKey } = useSelector((state) => state.auth)

  const { bookingRequests, loading: requestsLoading, error: requestsError } = useSelector((s) => s.adminBookingRequests)
  const { nearest, ranked }                                                  = useSelector((s) => s.adminFindNearestDriver)
  const { loading: accepting, success: acceptSuccess, error: acceptError }  = useSelector((s) => s.adminAcceptBooking)

  // UI state
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [acceptModal,     setAcceptModal]     = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [, setCountdownTick]                   = useState(0)
  const [modalRankedVehicles, setModalRankedVehicles] = useState([])
  const [modalFindingNearest, setModalFindingNearest] = useState(false)
  const [modalFindError, setModalFindError] = useState('')
  // TC-15 — distinguishes "all suitable vehicles busy" from "no such vehicle exists"
  const [modalBusyInfo, setModalBusyInfo] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [viewMode,        setViewMode]        = useState('table')
  const [activeTab,       setActiveTab]       = useState('unplanned')
  const [acceptedReqIds,  setAcceptedReqIds]  = useState(new Set())
  const [autoAcceptLogs,  setAutoAcceptLogs]  = useState([])

  // Data state
  const [unplannedBookings,        setUnplannedBookings]        = useState([])
  const [acceptedScheduleBookings, setAcceptedScheduleBookings] = useState([])
  const [loadingUnplanned,         setLoadingUnplanned]         = useState(false)
  const [loadingAcceptedSchedule,  setLoadingAcceptedSchedule]  = useState(false)

  // Filters — Unplanned
  const [unplannedReqId,     setUnplannedReqId]     = useState('')
  const [unplannedVehicleId, setUnplannedVehicleId] = useState('')
  const [unplannedDriverNo,  setUnplannedDriverNo]  = useState('')
  const [unplannedStatus,    setUnplannedStatus]    = useState('')
  const [unplannedDate,      setUnplannedDate]      = useState('')
  const [unplannedTonnage,   setUnplannedTonnage]   = useState('')

  // Filters — Scheduled
  const [scheduledReqId,     setScheduledReqId]     = useState('')
  const [scheduledVehicleId, setScheduledVehicleId] = useState('')
  const [scheduledDriverNo,  setScheduledDriverNo]  = useState('')
  const [scheduledStatus,    setScheduledStatus]    = useState('')
  const [scheduledDate,      setScheduledDate]      = useState('')
  const [scheduledTonnage,   setScheduledTonnage]   = useState('')

  // Filters — Accepted Schedule
  const [accSchedReqId,     setAccSchedReqId]     = useState('')
  const [accSchedVehicleId, setAccSchedVehicleId] = useState('')
  const [accSchedDriverNo,  setAccSchedDriverNo]  = useState('')
  const [accSchedStatus,    setAccSchedStatus]    = useState('')
  const [accSchedDate,      setAccSchedDate]      = useState('')
  const [accSchedTonnage,   setAccSchedTonnage]   = useState('')

  // Filtered results
  const [unplannedFiltered, setUnplannedFiltered] = useState([])
  const [scheduledFiltered, setScheduledFiltered] = useState([])
  const [accSchedFiltered,  setAccSchedFiltered]  = useState([])

  // Pagination
  const [unplannedCurrentPage, setUnplannedCurrentPage] = useState(1)
  const [scheduledCurrentPage, setScheduledCurrentPage] = useState(1)
  const [accSchedCurrentPage,  setAccSchedCurrentPage]  = useState(1)
  const rowsPerPage = 10

  // Auto-accept refs
  const autoAcceptTimersRef    = useRef({})
  const processedBookingsRef   = useRef(new Set())
  const autoAcceptIntervalRef  = useRef(null)
  const latestNearestDriverRef = useRef(null)
  const latestRankedDriversRef = useRef([])
  const isAutoAcceptingRef     = useRef(false)

  // WebSocket
  const wsRef           = useRef(null)
  const lastWsFetchRef  = useRef(0)
  const wsFetchTimerRef = useRef(null)
  const BOOKING_WS_URL  = 'wss://aeroviewbackend.dockyardsoftware.com:8095/Booking'

  // Track latest driver data from Redux
  useEffect(() => {
    if (nearest) {
      latestNearestDriverRef.current = nearest
    }
    if (ranked && ranked.length > 0) {
      latestRankedDriversRef.current = ranked
    }
  }, [nearest, ranked])

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => setViewMode(window.innerWidth < 768 ? 'card' : 'table')
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const normalizeDate = (dt) => {
    if (!dt) return ''
    const d = new Date(dt)
    return isNaN(d) ? '' : d.toISOString().split('T')[0]
  }

  const isScheduled = (b) => {
    const bs = (b.BookingStatus || '').toLowerCase()
    return bs.includes('shedule') || bs.includes('schedule') || bs === 's'
  }

  // ─── Fetch scheduled bookings (Redux)
  const fetchScheduledBookings = useCallback(async () => {
    try { await dispatch(FetchBookingRequests()) } catch (_) {}
  }, [dispatch])

  // ─── Fetch unplanned + accepted-schedule from main fork endpoint
  const fetchMainForkBookings = useCallback(async () => {
    if (!authKey) return
    setLoadingUnplanned(true)
    setLoadingAcceptedSchedule(true)
    try {
      const response = await fetch(
        'https://aeroviewbackend.dockyardsoftware.com/BookingRegister/GetMainForkBookingDetails',
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-key': authKey },
        }
      )
      const data = await response.json()

      if (data.statusCode === 200 && data.resultSet) {
        const all = data.resultSet

        // Extract all IDs from MainFork (accepted) table and inject them into local accepted state to fix the accept button still appeating after refresh bug
        const existingAcceptedIds = all.map(b => String(b.reqId || b.reqIdnew));
        console.log("EXISTING ACCEPTED IDS: ", existingAcceptedIds);
        setAcceptedReqIds(new Set(existingAcceptedIds))

        // Tab 1: Real-time Booking → unplanned
        const realTime = all.filter((b) => b.BookingStatus === 'Real-time Booking')
        const enrichedRealTime = await Promise.all(
          realTime.map(async (booking) => {
            let tonnage = ''
            if (booking.vId) {
              try {
                const vRes = await fetch(
                  `https://aeroviewbackend.dockyardsoftware.com/VehicleDetails/GetVehicleDetails?vehicleId=${encodeURIComponent(booking.vId)}`,
                  { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
                )
                const vData = await vRes.json()
                const vehicle = Array.isArray(vData.resultSet) ? vData.resultSet[0] : vData.resultSet || vData
                tonnage = vehicle?.weightCat || ''
              } catch (_) {}
            }
            return { ...booking, reqId: booking.bId, tonnage }
          })
        )
        setUnplannedBookings(enrichedRealTime)

        // Tab 3: Shedule Booking → accepted schedule
        const sheduleBookings = all.filter((b) => b.BookingStatus === 'Shedule Booking')
        const enrichedShedule = await Promise.all(
          sheduleBookings.map(async (booking) => {
            let tonnage = booking.tonnage || ''
            if (!tonnage && booking.vId) {
              try {
                const vRes = await fetch(
                  `https://aeroviewbackend.dockyardsoftware.com/VehicleDetails/GetVehicleDetails?vehicleId=${encodeURIComponent(booking.vId)}`,
                  { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
                )
                const vData = await vRes.json()
                const vehicle = Array.isArray(vData.resultSet) ? vData.resultSet[0] : vData.resultSet || vData
                tonnage = vehicle?.weightCat || ''
              } catch (_) {}
            }
            return { ...booking, reqId: booking.bId || booking.reqId, tonnage }
          })
        )
        enrichedShedule.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
        setAcceptedScheduleBookings(enrichedShedule)
      } else {
        setUnplannedBookings([])
        setAcceptedScheduleBookings([])
      }
    } catch (error) {
      console.error('Error fetching main fork bookings:', error)
      setUnplannedBookings([])
      setAcceptedScheduleBookings([])
    } finally {
      setLoadingUnplanned(false)
      setLoadingAcceptedSchedule(false)
    }
  }, [authKey])

  const fetchAllBookings = useCallback(async () => {
    await Promise.all([fetchMainForkBookings(), fetchScheduledBookings()])
  }, [fetchMainForkBookings, fetchScheduledBookings])

  useEffect(() => {
    if (authKey) fetchAllBookings()
  }, [authKey, fetchAllBookings])

  // WebSocket connection
  useEffect(() => {
    if (!authKey) return
    if (wsRef.current) wsRef.current.close()
    wsRef.current = new WebSocket(`${BOOKING_WS_URL}?token=${encodeURIComponent(authKey)}`)
    wsRef.current.onopen    = () => console.log('✅ Booking WebSocket Connected')
    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type || message.action || message.bId || message.bookingId) {
          const now    = Date.now()
          const minGap = 5000
          if (now - lastWsFetchRef.current >= minGap) {
            lastWsFetchRef.current = now
            fetchAllBookings()
          } else if (!wsFetchTimerRef.current) {
            const delay = minGap - (now - lastWsFetchRef.current)
            wsFetchTimerRef.current = setTimeout(() => {
              lastWsFetchRef.current  = Date.now()
              wsFetchTimerRef.current = null
              fetchAllBookings()
            }, delay)
          }
        }
      } catch (_) {}
    }
    wsRef.current.onerror = (e) => console.error('WebSocket Error:', e)
    wsRef.current.onclose = ()  => console.log('WebSocket Disconnected')
    return () => {
      wsRef.current?.close()
      wsRef.current = null
      if (wsFetchTimerRef.current) { clearTimeout(wsFetchTimerRef.current); wsFetchTimerRef.current = null }
    }
  }, [authKey, fetchAllBookings])

  // ─── Conflict detection
  // const unplannedConflictIds = useMemo(() =>
  //   getConflictingReqIds(unplannedBookings.filter(b => b.status === 'Pending')),
  // [unplannedBookings])

  // const scheduledConflictIds = useMemo(() => {
  //   const pending = Array.isArray(bookingRequests)
  //     ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending')
  //     : []
  //   return getConflictingReqIds(pending)
  // }, [bookingRequests])

  // New function to identify conflicts and blocks
  // CONFLICT: A pending booking overlaps with another pending booking. You can accept both, but accepting one will block the other.
  // BLOCK: A pening booking overlaps with a booking that has already been *accepted* (action disabled cuz the timeslot is taken)

  const getConflictAndBlockedIds = (pendingBookings, acceptedBookings) => {
    const conflicts = new Set();
    const blocked = new Set();

    // 1. Check Pending Vs. Accepted (BLOCKED)
    for (let i = 0; i < pendingBookings.length; i++) {
      const pending = pendingBookings[i];
      for (let j = 0; j < acceptedBookings.length; j++) {
        const accepted = acceptedBookings[j]
        if (normalizeTonnage(pending.tonnage) === normalizeTonnage(accepted.tonnage) && doTimesOverlap(pending, accepted)) {
          blocked.add(String(pending.reqId || pending.bId))
          break; // We only need one collision to mark this has blocked
        }
      }
    }

    // 2. Check Pending Vs. Pending (CONFLICT)
    for (let i = 0; i < pendingBookings.length; i++) {
      for (let j = i+1; j < pendingBookings.length; j++) {
        const a = pendingBookings[i];
        const b = pendingBookings[j];
        if (normalizeTonnage(a.tonnage) === normalizeTonnage(b.tonnage) && doTimesOverlap(a, b)) {
          conflicts.add(String(a.reqId || a.bId))
          conflicts.add(String(b.reqId || b.bId))
        }
      }
    }

    return { conflicts, blocked }
  }

  const allAcceptedBookings = useMemo(() => {
    const acceptedUnplanned = unplannedBookings.filter(b => b.status !== 'Pending' || b.accepted_by || acceptedReqIds.has(String(b.reqId || b.bId)))
    const scheduledList = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
    const acceptedScheduled = scheduledList.filter(b => b.status !== 'Pending' || b.accepted_by || acceptedReqIds.has(String(b.reqId || b.bId)))

    return [...acceptedUnplanned, ...acceptedScheduled, ...acceptedScheduleBookings]
  }, [unplannedBookings, bookingRequests, acceptedScheduleBookings, acceptedReqIds])

  const { conflicts: unplannedConflictIds, blocked: unplannedBlockedIds } = useMemo(() => {
    const pending = unplannedBookings.filter(b => b.status === 'Pending' && !acceptedReqIds.has(String(b.reqId || b.bId)))
    return getConflictAndBlockedIds(pending, allAcceptedBookings)
  }, [unplannedBookings, allAcceptedBookings, acceptedReqIds])

  const { conflicts: scheduledConflictIds, blocked: scheduledBlockedIds } = useMemo(() => {
    const pending = Array.isArray(bookingRequests)
      ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending' && !acceptedReqIds.has(String(b.reqId || b.bId)))
      : []
    return getConflictAndBlockedIds(pending, allAcceptedBookings)  
  }, [bookingRequests, allAcceptedBookings, acceptedReqIds])

  // const emptyConflictIds = useMemo(() => new Set(), [])
  const emptyIds = useMemo(() => new Set(), [])

  // ─── Filter: Unplanned
  useEffect(() => {
    let r = [...unplannedBookings]
    if (unplannedReqId.trim())     r = r.filter(b => String(b.reqId || b.bId || '').includes(unplannedReqId.trim()))
    if (unplannedVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(unplannedVehicleId.trim().toLowerCase()))
    if (unplannedDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(unplannedDriverNo.trim().toLowerCase()))
    if (unplannedTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(unplannedTonnage.trim().toLowerCase()))
    if (unplannedStatus)           r = r.filter(b => (b.status || '') === unplannedStatus)
    if (unplannedDate)             r = r.filter(b => normalizeDate(b.bookingDT) === unplannedDate)
    setUnplannedFiltered(r)
    setUnplannedCurrentPage(1)
  }, [unplannedReqId, unplannedVehicleId, unplannedDriverNo, unplannedTonnage, unplannedStatus, unplannedDate, unplannedBookings])

  // ─── Filter: Scheduled
  useEffect(() => {
    let r = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
    if (scheduledReqId.trim())     r = r.filter(b => String(b.reqId || b.bId || '').includes(scheduledReqId.trim()))
    if (scheduledVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(scheduledVehicleId.trim().toLowerCase()))
    if (scheduledDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(scheduledDriverNo.trim().toLowerCase()))
    if (scheduledTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(scheduledTonnage.trim().toLowerCase()))
    if (scheduledStatus)           r = r.filter(b => (b.status || '') === scheduledStatus)
    if (scheduledDate)             r = r.filter(b => normalizeDate(b.bookingDT) === scheduledDate)
    r.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
    setScheduledFiltered(r)
    setScheduledCurrentPage(1)
  }, [scheduledReqId, scheduledVehicleId, scheduledDriverNo, scheduledTonnage, scheduledStatus, scheduledDate, bookingRequests])

  // ─── Filter: Accepted Schedule
  useEffect(() => {
    let r = [...acceptedScheduleBookings]
    if (accSchedReqId.trim())     r = r.filter(b => String(b.bId || b.reqId || '').includes(accSchedReqId.trim()))
    if (accSchedVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(accSchedVehicleId.trim().toLowerCase()))
    if (accSchedDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(accSchedDriverNo.trim().toLowerCase()))
    if (accSchedTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(accSchedTonnage.trim().toLowerCase()))
    if (accSchedStatus)           r = r.filter(b => (b.status || '') === accSchedStatus)
    if (accSchedDate)             r = r.filter(b => normalizeDate(b.bookingDT) === accSchedDate)
    setAccSchedFiltered(r)
    setAccSchedCurrentPage(1)
  }, [accSchedReqId, accSchedVehicleId, accSchedDriverNo, accSchedTonnage, accSchedStatus, accSchedDate, acceptedScheduleBookings])

  // ─── Handlers
  // const handleOpenAcceptModal = async (booking) => {
  //   dispatch(ResetAdminBookingRequest())
  //   setSelectedVehicle(null)
  //   setAcceptModal(booking)
  //   setModalRankedVehicles([])
  //   setModalFindError('')
  //   setModalFindingNearest(true)
  //   try {
  //     const result = await dispatch(AdminFindNearestDriver(booking))
  //     const rankedVehicles = Array.isArray(result?.ranked) ? result.ranked : []
  //     const nearestVehicle = result?.nearest || rankedVehicles[0] || null
  //     setModalRankedVehicles(rankedVehicles)
  //     setSelectedVehicle(nearestVehicle)
  //     if (!nearestVehicle) setModalFindError('No matching vehicle found')
  //   } catch (err) {
  //     setModalRankedVehicles([])
  //     setSelectedVehicle(null)
  //     setModalFindError(err?.message || 'No matching vehicle found')
  //   } finally {
  //     setModalFindingNearest(false)
  //   }
  // }
  const handleOpenAcceptModal = async (booking) => {
    dispatch(ResetAdminBookingRequest())
    setSelectedVehicle(null)
    setAcceptModal(booking)
    setModalRankedVehicles([])
    setModalFindError('')
    setModalBusyInfo(null)
    setModalFindingNearest(true)

    // Stop auto accept timer immediately when admin opens the modal
    const bookingId = String(booking.reqId || booking.bId)
    if (autoAcceptTimersRef.current[bookingId]) {
      clearTimeout(autoAcceptTimersRef.current[bookingId])
      delete autoAcceptTimersRef.current[bookingId]
    }
    processedBookingsRef.current.add(bookingId)

    try {
      const result = await dispatch(AdminFindNearestDriver(booking))
      const rankedVehicles = Array.isArray(result?.ranked) ? result.ranked : []
      const nearestVehicle = result?.nearest || rankedVehicles[0] || null
      setModalRankedVehicles(rankedVehicles)
      setSelectedVehicle(nearestVehicle)
      if (!nearestVehicle) setModalFindError('No matching vehicle found')
    } catch (err) {
      setModalRankedVehicles([])
      setSelectedVehicle(null)
      if (err?.code === 'ALL_VEHICLES_BUSY') {
        setModalBusyInfo({ busyCount: err.busyCount || 0, nextFreeAt: err.nextFreeAt || null })
        setModalFindError('')
      } else {
        setModalBusyInfo(null)
        setModalFindError(err?.message || 'No matching vehicle found')
      }
    } finally {
      setModalFindingNearest(false)
    }
  }

  const handleCancelBooking = async (reqId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return
    try {
      await dispatch(AdminCancelBooking(reqId))
      await fetchAllBookings()
    } catch (err) {
      console.error(err)
      alert('❌ Cancel failed')
    }
  }

  const validateRequestInfo = async (reqId, key) => {
    try {
      const res = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/Getrequestinfo?rid=${reqId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': key } }
      )
      return await res.json()
    } catch (err) {
      console.error('Validation API error:', err)
      return false
    }
  }

  // ─── AUTO-ACCEPT FEATURE ────────────────────────────────────────────────────

  // Check if booking should auto-accept now (>= configured min past request placement)
  const shouldAutoAccept = useCallback((booking) => {
    if (!booking || booking.status !== 'Pending') return false
    // if (booking.accepted_by) return false

    const bookingId = String(booking.reqId || booking.bId)
    if (acceptedReqIds.has(bookingId)) return false;

    // const bookingId = String(booking.reqId || booking.bId)
    if (processedBookingsRef.current.has(bookingId)) return false
    const requestedAt = getRequestPlacedTime(booking)
    if (!requestedAt) return false
    const minutesAfterStart = (new Date() - requestedAt) / (1000 * 60)
    return minutesAfterStart >= AUTO_ACCEPT_MINUTES
  }, [acceptedReqIds])

  // Get human-readable time remaining until auto-accept (for badge display)
  const getAutoAcceptTimeRemaining = useCallback((booking) => {
    const requestedAt = getRequestPlacedTime(booking)
    if (!requestedAt) return null
    const now            = new Date()
    const autoAcceptAt   = new Date(requestedAt.getTime() + AUTO_ACCEPT_MINUTES * 60 * 1000)
    const remainingMs    = autoAcceptAt - now
    if (remainingMs <= 0) return 'now'
    const mins = Math.floor(remainingMs / 60000)
    const secs = Math.floor((remainingMs % 60000) / 1000)
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  }, [])

  // Return ms delay until the auto-accept threshold is reached
  const getAutoAcceptDelay = useCallback((booking) => {
    const requestedAt = getRequestPlacedTime(booking)
    if (!requestedAt) return null
    const delay = (requestedAt.getTime() + AUTO_ACCEPT_MINUTES * 60 * 1000) - Date.now()
    return Math.max(0, delay)
  }, [])

  // Whether a booking is within auto-accept window (show badge)
  const getWillAutoAccept = useCallback((booking) => {
    //if (booking.status !== 'Pending' || booking.accepted_by) return false

    const id = String(booking.reqId || booking.bId)

    if (
      booking.status !== 'Pending' || 
      acceptedReqIds.has(id)
    ) {
      return false
    }

    const requestedAt = getRequestPlacedTime(booking)
    if (!requestedAt) return false
    const remaining = new Date(requestedAt.getTime() + AUTO_ACCEPT_MINUTES * 60 * 1000) - new Date()
    return remaining > 0 && remaining <= AUTO_ACCEPT_MINUTES * 60 * 1000
  }, [acceptedReqIds])

  // Always resolve the newest booking object by id to avoid acting on stale timer snapshots.
  const getLatestPendingBookingById = useCallback((bookingId) => {
    const id = String(bookingId)
    const merged = [
      ...unplannedBookings,
      ...(Array.isArray(bookingRequests) ? bookingRequests : []),
    ]
    const latest = merged.find((b) => String(b.reqId || b.bId) === id)
    if (!latest) return null
    if (String(latest.status || '').toLowerCase() !== 'pending') return null
    // if (latest.accepted_by) return null
    if (acceptedReqIds.has(id)) return null;
    return latest
  }, [unplannedBookings, bookingRequests])

  // Core auto-accept function
  const autoAcceptBooking = useCallback(async (booking, retryCount = 0) => {
    const bookingId = String(booking.reqId || booking.bId)
    const latestBooking = getLatestPendingBookingById(bookingId)

    if (isAutoAcceptingRef.current) {
      setTimeout(() => autoAcceptBooking(booking, retryCount), 2000)
      return
    }
    if (!latestBooking) return
    if (processedBookingsRef.current.has(bookingId)) return

    isAutoAcceptingRef.current = true
    processedBookingsRef.current.add(bookingId)

    if (autoAcceptTimersRef.current[bookingId]) {
      clearTimeout(autoAcceptTimersRef.current[bookingId])
      delete autoAcceptTimersRef.current[bookingId]
    }

    const addLog = (msg) =>
      setAutoAcceptLogs(prev => [{ id: bookingId, time: new Date().toLocaleTimeString(), message: msg }, ...prev].slice(0, 10))

    try {
      // TC-02 — rank and claim as one indivisible step. Without the lock a
      // manually confirmed modal can commit in the middle of the poll loop
      // below, leaving this flow to assign a vehicle that is no longer free.
      const selectedDriver = await withAssignmentLock(async () => {
        addLog(`Finding nearest driver for booking ${bookingId}...`)
        await dispatch(AdminFindNearestDriver(latestBooking))

        // Poll for driver up to 15 s
        let driver = null
        for (let i = 0; i < 15 && !driver; i++) {
          await new Promise(r => setTimeout(r, 1000))
          if (latestNearestDriverRef.current)          driver = latestNearestDriverRef.current
          else if (latestRankedDriversRef.current[0])  driver = latestRankedDriversRef.current[0]
        }
        if (!driver) return null

        addLog(`Found driver ${driver.vehicleId} (${driver.distanceKm} km), accepting...`)
        await dispatch(AdminAcceptBooking(latestBooking, driver, authKey))
        return driver
      })

      if (selectedDriver) {
        await new Promise(r => setTimeout(r, 2000))
        const isValid = await validateRequestInfo(bookingId, authKey)
        if (isValid) {
          setAcceptedReqIds(prev => new Set([...prev, bookingId]))
          await fetchAllBookings()
          addLog(`✅ SUCCESS: Booking ${bookingId} auto-accepted with driver ${selectedDriver.vehicleId}`)
        } else {
          addLog(`⚠️ Validation failed for booking ${bookingId}`)
          processedBookingsRef.current.delete(bookingId)
        }
      } else {
        addLog(`⚠️ No drivers available for booking ${bookingId} — will retry in 60s`)
        processedBookingsRef.current.delete(bookingId)
        if (retryCount < 3) {
          setTimeout(() => {
            const refreshed = getLatestPendingBookingById(bookingId)
            if (refreshed) autoAcceptBooking(refreshed, retryCount + 1)
          }, 60000)
        } else {
          addLog(`❌ FAILED: Booking ${bookingId} — no drivers after ${retryCount + 1} attempts`)
        }
      }
    } catch (error) {
      // TC-02 — the chosen forklift was claimed by another request between
      // ranking and commit. Nothing is wrong with the booking itself, so
      // re-rank straight away (the taken vehicle is now excluded) instead of
      // sitting out the 30s backoff used for real failures.
      const wasTaken = error?.code === 'VEHICLE_TAKEN'
      addLog(
        wasTaken
          ? `🔁 Forklift ${error.vehicleId} taken by another request — re-ranking booking ${bookingId}`
          : error?.code === 'ALL_VEHICLES_BUSY'
            ? `⏳ All forklifts busy for booking ${bookingId} — will retry in 30s`
            : `❌ Error: ${error.message || 'Unknown error'} for booking ${bookingId}`
      )
      processedBookingsRef.current.delete(bookingId)
      if (retryCount < 3) {
        setTimeout(() => {
          const refreshed = getLatestPendingBookingById(bookingId)
          if (refreshed) autoAcceptBooking(refreshed, retryCount + 1)
        }, wasTaken ? 500 : 30000)
      }
    } finally {
      isAutoAcceptingRef.current = false
    }
  }, [dispatch, authKey, fetchAllBookings, getLatestPendingBookingById])

  // Set up per-booking timers
  const setupAutoAcceptTimers = useCallback((bookings) => {
    if (!Array.isArray(bookings)) return
    bookings
      .filter(b => 
        b.status === 'Pending' && 
        !b.accepted_by && 
        !acceptedReqIds.has(String(b.reqId || b.bId)) && 
        !processedBookingsRef.current.has(String(b.reqId || b.bId)))
      .forEach(booking => {
        const id = String(booking.reqId || booking.bId)
        if (autoAcceptTimersRef.current[id]) return
        if (shouldAutoAccept(booking)) {
          autoAcceptBooking(booking)
        } else {
          const delay = getAutoAcceptDelay(booking)
          if (delay !== null && delay > 0 && delay < 24 * 60 * 60 * 1000) {
            autoAcceptTimersRef.current[id] = setTimeout(() => {
              autoAcceptBooking(booking)
            }, delay)
          }
        }
      })
  }, [shouldAutoAccept, autoAcceptBooking, getAutoAcceptDelay])

  // Interval backup: sweep every 30 s for any bookings that slipped through
  const checkAndAutoAcceptExpiredBookings = useCallback(async () => {
    const allPending = [
      ...unplannedBookings.filter(b => b.status === 'Pending' && !b.accepted_by),
      ...(Array.isArray(bookingRequests)
        ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending' && !b.accepted_by)
        : []),
    ]
    const now = new Date()
    for (const booking of allPending) {
      const id = String(booking.reqId || booking.bId)
      if (processedBookingsRef.current.has(id) || isAutoAcceptingRef.current) continue
      const requestedAt = getRequestPlacedTime(booking)
      if (!requestedAt) continue
      if ((now - requestedAt) / (1000 * 60) >= AUTO_ACCEPT_MINUTES) {
        await autoAcceptBooking(booking)
      }
    }
  }, [unplannedBookings, bookingRequests, autoAcceptBooking])

  // Attach timers whenever pending bookings change
  useEffect(() => {
    const allPending = [
      ...unplannedBookings.filter(b => b.status === 'Pending' && !b.accepted_by),
      ...(Array.isArray(bookingRequests)
        ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending' && !b.accepted_by)
        : []),
    ]
    if (allPending.length > 0) setupAutoAcceptTimers(allPending)
  }, [unplannedBookings, bookingRequests, setupAutoAcceptTimers])

  // Interval check every 30 s
  useEffect(() => {
    if (autoAcceptIntervalRef.current) clearInterval(autoAcceptIntervalRef.current)
    autoAcceptIntervalRef.current = setInterval(checkAndAutoAcceptExpiredBookings, 30000)
    checkAndAutoAcceptExpiredBookings()
    return () => { if (autoAcceptIntervalRef.current) clearInterval(autoAcceptIntervalRef.current) }
  }, [checkAndAutoAcceptExpiredBookings])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(autoAcceptTimersRef.current).forEach(t => t && clearTimeout(t))
      autoAcceptTimersRef.current = {}
    }
  }, [])

  useEffect(() => {
    const countdownTimer = setInterval(() => {
      setCountdownTick((prev) => prev + 1)
    }, 1000)
    return () => clearInterval(countdownTimer)
  }, [])

  // ─── Manual Accept Modal handlers
  const handleConfirmAccept = async () => {
    if (!acceptModal || !selectedVehicle || isSubmitting) return
    setIsSubmitting(true)
    try {
      // TC-02 — the modal's ranking is a snapshot from whenever it was opened,
      // which may be minutes old. Take the assignment lock so this cannot
      // interleave with the auto-accept loop; AdminAcceptBooking then re-checks
      // availability at the commit point and rejects a stale pick.
      const result = await withAssignmentLock(() =>
        dispatch(AdminAcceptBooking(acceptModal, selectedVehicle, authKey))
      )

      console.log("[handleConfirm] status: ", result);

      const reqId   = String(acceptModal.reqId || acceptModal.bId);
      const isValid = await validateRequestInfo(reqId, authKey)
      if (!isValid) { alert('❌ Request validation failed.'); setIsSubmitting(false); return }

      setAcceptedReqIds(prev => new Set([...prev, String(reqId)]))

      processedBookingsRef.current.add(reqId);

      // Clear auto accept timeout when manually accepting
      if (autoAcceptTimersRef.current[reqId]) {
        clearTimeout(autoAcceptTimersRef.current[reqId])
        delete autoAcceptTimersRef.current[reqId];
      }

      // Close the modal immediately on success
      setAcceptModal(null)
      setSelectedVehicle(null)
      setModalRankedVehicles([])
      setModalFindError('')
      setModalFindingNearest(false)
      dispatch(ResetAdminBookingRequest())
    
      await fetchAllBookings()
    } catch (error) {
      console.error(error)
      if (error?.code === 'VEHICLE_TAKEN') {
        // Keep the modal open and re-rank so the admin can pick from what is
        // actually still free, rather than being told "something went wrong".
        setModalFindError('')
        setModalFindingNearest(true)
        setSelectedVehicle(null)
        try {
          const refreshed = await dispatch(AdminFindNearestDriver(acceptModal))
          const ranked = Array.isArray(refreshed?.ranked) ? refreshed.ranked : []
          setModalRankedVehicles(ranked)
          setSelectedVehicle(refreshed?.nearest || ranked[0] || null)
          setModalFindError(`Forklift ${error.vehicleId} was just assigned to another request — a replacement has been selected.`)
        } catch (findErr) {
          setModalRankedVehicles([])
          setModalFindError(
            findErr?.code === 'ALL_VEHICLES_BUSY'
              ? 'That forklift was just taken and every other suitable one is busy for this slot.'
              : findErr?.message || 'That forklift was just taken and no replacement is available.'
          )
        } finally {
          setModalFindingNearest(false)
        }
      } else {
        alert('❌ Something went wrong')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // const handleCloseAcceptModal = () => {
  //   setAcceptModal(null)
  //   setSelectedVehicle(null)
  //   setModalRankedVehicles([])
  //   setModalFindError('')
  //   setModalFindingNearest(false)
  //   setIsSubmitting(false)
  //   dispatch(ResetAdminBookingRequest())
  // }

  const handleCloseAcceptModal = () => {
  if (acceptModal) {
    const bookingId = String(acceptModal.reqId || acceptModal.bId)
    processedBookingsRef.current.delete(bookingId)
  }

  setAcceptModal(null)
  setSelectedVehicle(null)
  setModalRankedVehicles([])
  setModalFindError('')
  setModalFindingNearest(false)
  setIsSubmitting(false)
  dispatch(ResetAdminBookingRequest())
}

  // ─── Stats
  const scheduledList  = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
  const allBookings    = [...unplannedBookings, ...scheduledList]
  const totalCount     = unplannedBookings.length + scheduledList.length
  const pendingCount   = allBookings.filter(b => b.status === 'Pending').length
  const acceptedCount  = allBookings.filter(b => b.status === 'Active' || b.status === 'Accepted').length

  const statusColor = (s) => {
    switch (s) {
      case 'Active':      return 'bg-emerald-50 text-emerald-700'
      case 'Pending':     return 'bg-yellow-50 text-yellow-700'
      case 'Completed':   return 'bg-blue-50 text-blue-700'
      case 'Enroute':     return 'bg-purple-50 text-purple-700'
      case 'In Progress': return 'bg-indigo-50 text-indigo-700'
      case 'Cancel':      return 'bg-red-50 text-red-700'
      default:            return 'bg-gray-50 text-gray-700'
    }
  }

  const statusOptions = ['Pending', 'Active', 'Completed', 'Enroute', 'In Progress', 'Cancel']
  const isLoadingAny  = loadingUnplanned || loadingAcceptedSchedule || requestsLoading

  // ─── Reusable filter bar builder
  const buildFilterBar = ({ totalLabel, count, filters, hasActive, onClear }) => (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {filters}
      </div>
      {hasActive && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing <span className="font-semibold text-gray-700">{count}</span> {totalLabel}
          </p>
          <button
            onClick={onClear}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <X size={12} />Clear all filters
          </button>
        </div>
      )}
    </div>
  )

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <ClipboardList size={22} className="text-blue-600" />
              </div>
              Booking Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track all booking requests with real-time updates
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700">Live Updates Active</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full border border-purple-200">
              <Bell size={12} className="text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Auto-Accept Active (3 min)</span>
            </div>
            <button
              onClick={fetchAllBookings}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Auto-Accept Activity Log */}
      {autoAcceptLogs.length > 0 && (
        <div className="mb-4 bg-gray-50 rounded-xl border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
              <Bell size={12} />Auto-Accept Activity Log
            </h3>
            <button
              onClick={() => setAutoAcceptLogs([])}
              className="text-xs text-gray-400 hover:text-gray-600 transition"
            >
              Clear
            </button>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {autoAcceptLogs.map((log, idx) => (
              <div key={idx} className="text-xs text-gray-600 font-mono">
                <span className="text-gray-400">[{log.time}]</span> {log.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid — 6 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard title="Total Requests"   value={totalCount}                       icon={ClipboardList}  color="blue"   />
        <StatCard title="Unplanned"        value={unplannedBookings.length}         icon={Zap}            color="purple" />
        <StatCard title="Scheduled"        value={scheduledList.length}             icon={CalendarDays}   color="indigo" />
        <StatCard title="Acc. Schedule"    value={acceptedScheduleBookings.length}  icon={CalendarCheck}  color="teal"   />
        <StatCard title="Pending"          value={pendingCount}                     icon={Clock}          color="yellow" />
        <StatCard title="Accepted"         value={acceptedCount}                    icon={CheckCircle}    color="green"  />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 mb-5 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <span className="text-gray-600">Conflict — Overlapping time slot</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <span className="text-gray-600">Blocked — Another overlapping booking accepted</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-400"></div>
          <span className="text-gray-600">Auto-Accept — Will be auto-accepted after 3 min from request time</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
          <span className="text-gray-600">Live — Real-time updates</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <TabButton
          active={activeTab === 'unplanned'}
          onClick={() => setActiveTab('unplanned')}
          icon={Zap}
          count={unplannedFiltered.length}
        >
          Unplanned Bookings
        </TabButton>
        <TabButton
          active={activeTab === 'scheduled'}
          onClick={() => setActiveTab('scheduled')}
          icon={CalendarDays}
          count={scheduledFiltered.length}
        >
          Scheduled Bookings
        </TabButton>
        <TabButton
          active={activeTab === 'acceptedSchedule'}
          onClick={() => setActiveTab('acceptedSchedule')}
          icon={CalendarCheck}
          count={accSchedFiltered.length}
          activeColor="teal"
        >
          Accepted Schedule
        </TabButton>
      </div>

      {/* ══ TAB 1: UNPLANNED ══ */}
      {activeTab === 'unplanned' && (
        <BookingSection
          isLoading={isLoadingAny}
          error={null}
          filteredData={unplannedFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={unplannedCurrentPage}
          setCurrentPage={setUnplannedCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={unplannedConflictIds}
          blockedIds={unplannedBlockedIds}
          acceptedReqIds={acceptedReqIds}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={false}
          showTonnage={false}
          readOnly={false}
          reqIdLabel="Booking ID"
          getWillAutoAccept={getWillAutoAccept}
          getAutoAcceptTimeRemaining={getAutoAcceptTimeRemaining}
          filterBar={buildFilterBar({
            totalLabel: 'unplanned requests',
            count: unplannedFiltered.length,
            hasActive: !!(unplannedReqId || unplannedVehicleId || unplannedDriverNo || unplannedTonnage || unplannedStatus || unplannedDate),
            onClear: () => { setUnplannedReqId(''); setUnplannedVehicleId(''); setUnplannedDriverNo(''); setUnplannedTonnage(''); setUnplannedStatus(''); setUnplannedDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Booking ID"  value={unplannedReqId}     onChange={setUnplannedReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={unplannedVehicleId} onChange={setUnplannedVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={unplannedDriverNo}  onChange={setUnplannedDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={unplannedTonnage}   onChange={setUnplannedTonnage}   icon={Weight} />
                <FilterSelect value={unplannedStatus}   onChange={setUnplannedStatus} options={statusOptions} />
                <FilterDate   value={unplannedDate}     onChange={setUnplannedDate} />
              </>
            ),
          })}
        />
      )}

      {/* ══ TAB 2: SCHEDULED ══ */}
      {activeTab === 'scheduled' && (
        <BookingSection
          isLoading={isLoadingAny}
          error={requestsError}
          filteredData={scheduledFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={scheduledCurrentPage}
          setCurrentPage={setScheduledCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={scheduledConflictIds}
          blockedIds={scheduledBlockedIds}
          acceptedReqIds={acceptedReqIds}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={true}
          showTonnage={true}
          readOnly={false}
          reqIdLabel="Req ID"
          getWillAutoAccept={getWillAutoAccept}
          getAutoAcceptTimeRemaining={getAutoAcceptTimeRemaining}
          filterBar={buildFilterBar({
            totalLabel: 'scheduled requests',
            count: scheduledFiltered.length,
            hasActive: !!(scheduledReqId || scheduledVehicleId || scheduledDriverNo || scheduledTonnage || scheduledStatus || scheduledDate),
            onClear: () => { setScheduledReqId(''); setScheduledVehicleId(''); setScheduledDriverNo(''); setScheduledTonnage(''); setScheduledStatus(''); setScheduledDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Request ID"  value={scheduledReqId}     onChange={setScheduledReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={scheduledVehicleId} onChange={setScheduledVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={scheduledDriverNo}  onChange={setScheduledDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={scheduledTonnage}   onChange={setScheduledTonnage}   icon={Weight} />
                <FilterSelect value={scheduledStatus}   onChange={setScheduledStatus} options={statusOptions} />
                <FilterDate   value={scheduledDate}     onChange={setScheduledDate} />
              </>
            ),
          })}
        />
      )}

      {/* ══ TAB 3: ACCEPTED SCHEDULE ══ */}
      {activeTab === 'acceptedSchedule' && (
        <BookingSection
          isLoading={loadingAcceptedSchedule}
          error={null}
          filteredData={accSchedFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={accSchedCurrentPage}
          setCurrentPage={setAccSchedCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={emptyIds}
          blockedIds={emptyIds}
          acceptedReqIds={new Set()}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={false}
          showTonnage={false}
          readOnly={true}
          reqIdLabel="Booking ID"
          filterBar={buildFilterBar({
            totalLabel: 'accepted schedule bookings',
            count: accSchedFiltered.length,
            hasActive: !!(accSchedReqId || accSchedVehicleId || accSchedDriverNo || accSchedTonnage || accSchedStatus || accSchedDate),
            onClear: () => { setAccSchedReqId(''); setAccSchedVehicleId(''); setAccSchedDriverNo(''); setAccSchedTonnage(''); setAccSchedStatus(''); setAccSchedDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Booking ID"  value={accSchedReqId}     onChange={setAccSchedReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={accSchedVehicleId} onChange={setAccSchedVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={accSchedDriverNo}  onChange={setAccSchedDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={accSchedTonnage}   onChange={setAccSchedTonnage}   icon={Weight} />
                <FilterSelect value={accSchedStatus}    onChange={setAccSchedStatus} options={statusOptions} />
                <FilterDate   value={accSchedDate}      onChange={setAccSchedDate} />
              </>
            ),
          })}
        />
      )}

      {/* ─── Booking Detail Modal ────────────────────────────────────────────── */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <ClipboardList size={18} className="text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Booking Details</h2>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoField label="Request ID"      value={selectedBooking.reqId || selectedBooking.bId} />
                <InfoField label="Status"          value={selectedBooking.status} badge={statusColor(selectedBooking.status)} />
                <InfoField label="Booked By"       value={selectedBooking.bookingSno} />
                <InfoField label="Tonnage"         value={selectedBooking.tonnage ? `${selectedBooking.tonnage} tons` : '-'} />
                <InfoField label="Vehicle ID"      value={selectedBooking.vId   || '-'} />
                <InfoField label="Driver No"       value={selectedBooking.dSNo  || '-'} />
                <InfoField label="Booking Type"    value={selectedBooking.BookingStatus || '-'} />
                <InfoField label="Booking Date"    value={normalizeDate(selectedBooking.bookingDT)} />
                <InfoField label="From"            value={selectedBooking.From  || '-'} />
                <InfoField label="To"              value={selectedBooking.TO    || '-'} />
                <InfoField label="Start Location"  value={`${selectedBooking.slatitude || '-'}, ${selectedBooking.slongitude || '-'}`} />
                <InfoField label="End Location"    value={`${selectedBooking.elatitude || '-'}, ${selectedBooking.elongitude || '-'}`} />
                <InfoField label="Scheduled Start" value={selectedBooking.bsTime ? new Date(selectedBooking.bsTime).toLocaleString() : '-'} />
                <InfoField label="Scheduled End"   value={selectedBooking.beTime ? new Date(selectedBooking.beTime).toLocaleString() : '-'} />
              </div>
              <div className="mt-3">
                <InfoField label="Remark" value={selectedBooking.Remark || '-'} />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              {selectedBooking.status === 'Pending' && !selectedBooking.accepted_by &&
                selectedBooking.BookingStatus !== 'Shedule Booking' && (
                <button
                  onClick={() => { setSelectedBooking(null); handleOpenAcceptModal(selectedBooking) }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition"
                >
                  <CheckCircle size={16} />Accept & Assign Driver
                </button>
              )}
              <button
                onClick={() => setSelectedBooking(null)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Accept & Assign Modal ───────────────────────────────────────────── */}
      {acceptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle size={18} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Accept & Assign Booking</h2>
              </div>
              <button onClick={handleCloseAcceptModal} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              {/* Booking Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <ClipboardList size={14} />Booking Summary
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Request ID:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.reqId || acceptModal.bId}</p></div>
                  <div><span className="text-gray-500">Tonnage:</span><p className="font-semibold text-orange-600 mt-0.5">{acceptModal.tonnage} tons</p></div>
                  <div><span className="text-gray-500">From:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.From || '—'}</p></div>
                  <div><span className="text-gray-500">To:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.TO || '—'}</p></div>
                  <div><span className="text-gray-500">Booked By:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bookingSno}</p></div>
                  <div><span className="text-gray-500">Scheduled Start:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bsTime ? new Date(acceptModal.bsTime).toLocaleString() : '-'}</p></div>
                  <div><span className="text-gray-500">Destination Lat:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elatitude || '-'}</p></div>
                  <div><span className="text-gray-500">Destination Lon:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elongitude || '-'}</p></div>
                </div>
              </div>

              {/* Vehicle Selection — only nearest is selectable */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Truck size={14} />Select Vehicle to Assign
                </h3>

                {modalFindingNearest && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                    <RefreshCw size={18} className="text-blue-600 animate-spin" />
                    <span className="text-sm text-blue-700">Finding matching vehicles...</span>
                  </div>
                )}

                {/* TC-15 — every suitable forklift is occupied for this slot */}
                {modalBusyInfo && !modalFindingNearest && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <Clock size={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        All Forklifts Are Busy
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {modalBusyInfo.busyCount > 0
                          ? `All ${modalBusyInfo.busyCount} suitable forklift${modalBusyInfo.busyCount > 1 ? 's are' : ' is'} occupied for this time slot.`
                          : 'All suitable forklifts are occupied for this time slot.'}
                        {' '}This request stays pending — assign it once one becomes free, or change the requested time.
                      </p>
                      {modalBusyInfo.nextFreeAt && (
                        <p className="text-xs font-medium text-amber-800 mt-2">
                          Next expected free:{' '}
                          {new Date(modalBusyInfo.nextFreeAt).toLocaleString([], {
                            day: '2-digit', month: 'short',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {modalFindError && !modalFindingNearest && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                    <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">No Matching Vehicle Found</p>
                      <p className="text-xs text-red-600 mt-1">{modalFindError}</p>
                    </div>
                  </div>
                )}

                {modalRankedVehicles.length > 0 && !modalFindingNearest && (
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                    {modalRankedVehicles.map((v, idx) => {
                      const isSelected = selectedVehicle?.vehicleId === v.vehicleId
                      const isNearest  = idx === 0
                      const isDisabled = !isNearest
                      return (
                        <div
                          key={v.vehicleId}
                          onClick={() => isNearest && setSelectedVehicle(v)}
                          className={`rounded-xl border-2 p-4 transition-all ${
                            isDisabled
                              ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200'
                              : isSelected
                              ? 'cursor-pointer border-emerald-500 bg-emerald-50'
                              : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {isSelected
                                ? <CheckCircle size={18} className="text-emerald-600" />
                                : <div className={`w-4 h-4 rounded-full border-2 ${isDisabled ? 'border-gray-200' : 'border-gray-300'}`} />
                              }
                              <span className={`text-base font-semibold ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{v.vehicleId}</span>
                              {isNearest && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                                  Nearest ✓ Only selectable
                                </span>
                              )}
                              {isDisabled && (
                                <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
                                  Not available
                                </span>
                              )}
                            </div>
                            <span className={`text-sm font-semibold ${isDisabled ? 'text-gray-400' : 'text-blue-600'}`}>{v.distanceKm} km</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 ml-7">
                            <div><span className="text-gray-400">Driver:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : ''}`}>{v.driverSno}</span></div>
                            <div><span className="text-gray-400">Weight:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : 'text-orange-600'}`}>{v.weightCat}</span></div>
                            <div><span className="text-gray-400">Type:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : ''}`}>{v.vehicleType}</span></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {acceptError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <AlertCircle size={14} />{acceptError}
                  </p>
                </div>
              )}

              {acceptSuccess && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <p className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle size={14} />Booking accepted — request sent to driver successfully.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                onClick={handleCloseAcceptModal}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAccept}
                disabled={!selectedVehicle || modalFindingNearest || accepting || isSubmitting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(accepting || isSubmitting) ? (
                  <><RefreshCw size={16} className="animate-spin" />Sending to Driver...</>
                ) : (
                  <><CheckCircle size={16} />Accept & Assign {selectedVehicle ? `(${selectedVehicle.vehicleId})` : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}